import { create } from 'zustand'
import { indexedDBManager, type CustomValue } from '@creanizer/utils'
import type { NameType } from '@creanizer/types'

interface CustomValuesState {
  customValues: CustomValue[]
  isLoading: boolean
  
  // 初期化
  init: () => Promise<void>
  
  // カスタム値の追加
  addCustomValue: (
    type: CustomValue['type'],
    value: string,
    label: string,
    generatorType?: CustomValue['generatorType'],
    nameType?: string
  ) => Promise<void>
  
  // カスタム値の取得
  getCustomValues: (
    type?: CustomValue['type'],
    generatorType?: CustomValue['generatorType'],
    nameType?: string
  ) => CustomValue[]
  
  // カスタム値の使用回数更新
  useCustomValue: (id: string) => Promise<void>
  
  // カスタム値の削除
  deleteCustomValue: (id: string) => Promise<void>
  
  // 全カスタム値の削除
  clearCustomValues: () => Promise<void>
  
  // エクスポート
  exportCustomValues: () => Promise<string>
  
  // インポート
  importCustomValues: (data: string) => Promise<void>
}

export const useCustomValuesStore = create<CustomValuesState>((set, get) => ({
  customValues: [],
  isLoading: true,

  init: async () => {
    try {
      set({ isLoading: true })
      await indexedDBManager.init()
      const values = await indexedDBManager.getCustomValues()
      set({ customValues: values, isLoading: false })
    } catch (error) {
      console.error('Failed to initialize custom values:', error)
      set({ isLoading: false })
    }
  },

  addCustomValue: async (type, value, label, generatorType, nameType) => {
    try {
      const newValue = await indexedDBManager.addCustomValue({
        type,
        value,
        label,
        generatorType,
        nameType,
      })
      set(state => ({
        customValues: [...state.customValues, newValue]
      }))
    } catch (error) {
      console.error('Failed to add custom value:', error)
      throw error
    }
  },

  getCustomValues: (type, generatorType, nameType) => {
    const { customValues } = get()
    let filtered = customValues
    
    if (type) {
      filtered = filtered.filter(v => v.type === type)
    }
    
    if (generatorType) {
      filtered = filtered.filter(v => v.generatorType === generatorType)
    }
    
    if (nameType) {
      filtered = filtered.filter(v => v.nameType === nameType)
    }
    
    return filtered
  },

  useCustomValue: async (id) => {
    try {
      await indexedDBManager.updateCustomValueUsage(id)
      set(state => ({
        customValues: state.customValues.map(v => 
          v.id === id 
            ? { ...v, usageCount: v.usageCount + 1, lastUsedAt: new Date() }
            : v
        ).sort((a, b) => b.usageCount - a.usageCount)
      }))
    } catch (error) {
      console.error('Failed to update custom value usage:', error)
    }
  },

  deleteCustomValue: async (id) => {
    try {
      await indexedDBManager.deleteCustomValue(id)
      set(state => ({
        customValues: state.customValues.filter(v => v.id !== id)
      }))
    } catch (error) {
      console.error('Failed to delete custom value:', error)
      throw error
    }
  },

  clearCustomValues: async () => {
    try {
      const { customValues } = get()
      for (const value of customValues) {
        await indexedDBManager.deleteCustomValue(value.id)
      }
      set({ customValues: [] })
    } catch (error) {
      console.error('Failed to clear custom values:', error)
      throw error
    }
  },

  exportCustomValues: async () => {
    const { customValues } = get()
    return JSON.stringify({
      version: 1,
      exportedAt: new Date().toISOString(),
      customValues,
    }, null, 2)
  },

  importCustomValues: async (data) => {
    try {
      const parsed = JSON.parse(data)
      if (!parsed.customValues || !Array.isArray(parsed.customValues)) {
        throw new Error('Invalid import data')
      }
      
      const newValues: CustomValue[] = []
      for (const value of parsed.customValues) {
        const newValue = await indexedDBManager.addCustomValue({
          type: value.type,
          value: value.value,
          label: value.label,
          nameType: value.nameType,
        })
        newValues.push(newValue)
      }
      
      set(state => ({
        customValues: [...state.customValues, ...newValues]
      }))
    } catch (error) {
      console.error('Failed to import custom values:', error)
      throw error
    }
  },
}))